import { useMutation, useQuery } from '@tanstack/react-query';
import { ConnectGhesForm } from '@linearb/linui-common';
import axios from 'common/requester';
import { API } from 'common/constants/api-urls';
import { createSensorAuthorization } from 'common/services/sensors.service';
import { AUTH_LEVEL } from 'common/constants/constants';
import { useCallback } from 'react';
import CenteredLoader from 'components/CenteredLoader.style';
import { useDispatch } from 'react-redux';
import { updateSensorAuthorizationSucceed } from 'state/actions';
import { getSensorAuthorizations } from 'state/callers';

/**
 * Represents an organization webhook.
 */
interface OrgWebhook {
  status: string;
  webhook_url: string;
  webhook_secret: string;
  message: string;
}

/**
 * Represents the props for the ConnectGhesFormWrapper component.
 */
interface ConnectGhseFormWrapperProps {
  self_hosted_uri: string;
  sensor_id: string;
  sensor_auth_id: string;
  onClose: () => void;
  integration: any;
}

/**
 * Retrieves the organization level webhook.
 * @returns {Promise<OrgWebhook>} A promise that resolves to the organization level webhook.
 */
const getOrgLevelWebhook = async (): Promise<OrgWebhook> => {
  const response = await axios.post(API.ORG_WEBHOOKS);
  // @ts-ignore
  return response;
};

/**
 * Wrapper component for connecting to GitHub Enterprise Server form.
 */
const ConnectGhesFormWrapper = (props: ConnectGhseFormWrapperProps) => {
  const dispatch = useDispatch();

  // Use the useQuery hook to get org webhook data
  const { data, isLoading, error } = useQuery(
    ['orgLevelWebhook'],
    getOrgLevelWebhook
  );

  /**
   * Validates the GitHub App credentials.
   * @param {object} params - The validation parameters.
   * @param {string} params.appId - The ID of the GitHub App.
   * @param {string} params.privateKey - The private key of the GitHub App.
   * @returns {Promise<boolean>} A promise that resolves to true if the GitHub App is valid, false otherwise.
   */
  const validateGithubApp = useCallback(
    async ({ appId, privateKey }) => {
      const response = await axios.post(`${API.APP_VALIDATE_PATH}/github`, {
        app_id: appId,
        private_key: privateKey.value,
        self_hosted_uri: props.self_hosted_uri
      });

      // @ts-ignore
      const { token_status } = response;

      return token_status === 'valid';
    },
    [props.self_hosted_uri]
  );

  /**
   * Handles the form submission.
   * @param {object} params - The form data.
   * @param {string} params.appId - The ID of the GitHub App.
   * @param {string} params.privateKey - The private key of the GitHub App.
   * @returns {Promise<void>} A promise that resolves when the submission is complete.
   */
  const handleSubmit = useCallback(
    async ({ appId, privateKey }) => {
      const { self_hosted_uri, sensor_id, sensor_auth_id } = props;

      const body = {
        state: 'ok',
        name: 'gitstream github app',
        sensor_id,
        token: {
          app_id: appId,
          private_key: privateKey.value,
          self_hosted_uri
        },
        profile: { auth_type: 'github_app' },
        authorization_level: AUTH_LEVEL.ORGANIZATION
      };

      const childSensorAuth = await createSensorAuthorization(body, {
        parent_sensor_auth_id: sensor_auth_id
      });

      const parentSensor = {
        ...props.integration,
        // @ts-ignore
        child_sensor_auth_id: childSensorAuth.id
      };
      dispatch(updateSensorAuthorizationSucceed(parentSensor));
    },
    [props.self_hosted_uri, props.sensor_id, props.sensor_auth_id]
  );

  const { onClose } = props;

  const { mutate } = useMutation(['connectGHESonSubmit'], handleSubmit, {
    onSuccess: async () => {
      await dispatch(getSensorAuthorizations({ withGitStreamEnabled: true }));
      onClose();
    }
  });

  if (isLoading) {
    return <CenteredLoader />;
  }

  const {
    webhook_url: webhookUrl,
    webhook_secret: webhookSecret,
    status,
    message
  } = data;

  if (error || status === 'error') {
    return <div>Error: {message}</div>;
  }

  const gsWebhookUrl =
    webhookUrl !== '' ? `${webhookUrl}?source=gitstream` : '';

  return (
    <ConnectGhesForm
      webhookUrl={gsWebhookUrl}
      webhookSecret={webhookSecret}
      onCancel={onClose}
      onSubmit={mutate}
      validateGithubApp={validateGithubApp}
    />
  );
};

export default ConnectGhesFormWrapper;
